/**
 * Карточка скалярных показателей раздела: «показатель · источник · значение ·
 * единица» (US-39 §8).
 *
 * У каждого показателя — бейдж слоя, из которого взято значение (FR-8), у
 * показателя с объявленным источником — тумблер источника (FR-10). Ввод открыт
 * только на «Ручном вводе»: на любом источнике поле заблокировано и рядом замок
 * — в том числе когда источник значения не дал (FR-11, FR-11.1).
 */
import { Lock } from 'lucide-react';

import { Card, CardContent, CardHeader } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/cn';
import type {
  Dtype,
  PassportField,
  SourceOption,
} from '../../../../requests/input_data.request';
import { pick, type Lang } from '../formState';
import { isLocked, sourceStateOf } from '../sources';
import { ManualNote, SourceBadge, SourceSelect } from './SourceControls';

export interface FieldsCardProps {
  /** Заголовок раздела. Пустая строка — не показывать: он уже есть над карточкой. */
  title: string;
  fields: PassportField[];
  lang: Lang;
  values: Record<string, string>;
  initial: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Текущий выбор источника по ключу показателя. */
  modes: Record<string, string | null>;
  onSourceChange: (key: string, mode: string) => void;
  /** Варианты прежнего двоичного тумблера — для бэкенда до US-39. */
  legacyOptions: { snp: SourceOption; manual: SourceOption };
  /** Кто смотрит: подпись автора правки показывается только своему. */
  currentUserId: string | null;
  /** Почему значение не сохранено — по ключу показателя, готовым текстом. */
  issues: Record<string, string>;
}

export const inputTypeOf = (dtype: Dtype): string => {
  if (dtype === 'date') return 'date';
  if (dtype === 'str') return 'text';
  return 'number';
};

/** Введённое оператором — строкой для подписи; `null` — ничего не введено. */
const manualValueText = (value: unknown): string | null => {
  if (typeof value === 'number') return Intl.NumberFormat('ru-RU').format(value);
  if (typeof value === 'string' && value !== '') return value;
  return null;
};

export const FieldsCard = ({
  title,
  fields,
  lang,
  values,
  initial,
  onChange,
  modes,
  onSourceChange,
  legacyOptions,
  currentUserId,
  issues,
}: FieldsCardProps) => {
  if (fields.length === 0) return null;

  return (
    <Card className="shadow-card" data-testid="fields-card">
      {title !== '' && (
        <CardHeader className="pb-3">
          <h3 className="text-base font-semibold">{title}</h3>
        </CardHeader>
      )}
      {/* Без заголовка карточке нужен свой верхний отступ: `pt-0` рассчитан на
          шапку выше. */}
      <CardContent className={cn('pt-0', title === '' && 'pt-4')}>
        <ul className="divide-y divide-surface-border">
          {fields.map((field) => {
            const source = sourceStateOf(field, legacyOptions);
            const mode = modes[field.key] ?? source.mode;
            const locked = isLocked(source, mode);
            const value = values[field.key] ?? '';
            const changed = value !== (initial[field.key] ?? '');
            const label = pick(field.label_ru, field.label_kz, lang);
            const issue = issues[field.key];
            return (
              <li key={field.key} className="space-y-1 py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {/* Подпись встаёт в строку с контролами, только когда им
                      хватает места; до 2xl она — отдельной строкой сверху. */}
                  <span className="min-w-0 basis-full text-sm 2xl:flex-1 2xl:basis-0">
                    {label}
                    {field.required === true && (
                      <span className="ml-0.5 text-status-danger" aria-hidden="true">
                        *
                      </span>
                    )}
                  </span>
                  <span className="ml-auto flex flex-wrap items-center justify-end gap-2">
                    <SourceBadge
                      source={field.source}
                      lang={lang}
                      testId={`badge-${field.key}`}
                    />
                    <SourceSelect
                      options={source.options}
                      value={mode}
                      lang={lang}
                      testId={`source-${field.key}`}
                      onChange={(next) => onSourceChange(field.key, next)}
                    />
                    <span className="flex shrink-0 items-center gap-1.5">
                      {locked && (
                        <Lock
                          className="size-3.5 shrink-0 text-text-muted"
                          aria-hidden="true"
                          data-testid={`lock-${field.key}`}
                        />
                      )}
                      {field.dtype === 'bool' ? (
                        <input
                          type="checkbox"
                          checked={value === 'true'}
                          disabled={locked}
                          aria-label={label}
                          data-testid={`input-${field.key}`}
                          onChange={(event) =>
                            onChange(field.key, event.target.checked ? 'true' : 'false')
                          }
                          className="size-5"
                        />
                      ) : (
                        <Input
                          type={inputTypeOf(field.dtype)}
                          inputMode={field.dtype === 'str' ? undefined : 'decimal'}
                          value={value}
                          disabled={locked}
                          aria-label={label}
                          aria-invalid={issue !== undefined}
                          data-testid={`input-${field.key}`}
                          onChange={(event) => onChange(field.key, event.target.value)}
                          className={cn(
                            'h-9 w-40 text-right tabular-nums',
                            changed && 'border-brand-500 bg-brand-50',
                            locked && 'bg-surface-muted text-text-muted',
                            issue !== undefined && 'border-status-danger',
                          )}
                        />
                      )}
                    </span>
                    <span className="w-16 shrink-0 text-xs text-text-muted">
                      {field.unit ?? ''}
                    </span>
                  </span>
                </div>
                <ManualNote
                  value={manualValueText(field.manual_value)}
                  periodFrom={field.period_from}
                  periodTo={field.period_to}
                  updatedBy={field.updated_by}
                  currentUserId={currentUserId}
                  testId={`manual-note-${field.key}`}
                  className="text-right"
                />
                {issue !== undefined && (
                  <p
                    className="text-right text-xs text-status-danger"
                    data-testid={`issue-${field.key}`}
                  >
                    {issue}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};
